import { USER_ROLES } from '@/constants/app';

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

export interface User {
  id: string;
  name: string;
  role: UserRole;
  email: string;
  mobile?: string;
  address: string;
  city?: string;
  state?: string;
  pincode: string;
  country?: string;
  dateOfBirth: string;
  dateOfJoining?: string;
  isActive?: boolean;
  isEmailVerified: boolean;
  isWhatsappVerified?: boolean;
  membershipTier: string;
  lastLogin: string;
  createdAt: string;
  profilePicture?: {
    id:string;
    url:string;
    originalName:string
  }
  
  // Profile fields
  designation?: string | {
    _id: string;
    name: string;
    isActive: boolean;
    __v: number;
    createdAt: string;
    updatedAt: string;
  };
  whatsappNumber?: string;
  aboutMe?: string;
  socialMediaLinks?: string[];
  profileImage?: string;
  gender?: 'Male' | 'Female' | 'Other';
     stats?: {
                feedCount: number| null,
                followersCount: number| null,
                followingCount: number| null,
            };
  // Current company information
  currentCompanyInfo?: {
    companyname: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pincode: string;
    companyUrl?:string;
  };
  
  // Job seeking fields
  isLookingForJob?: boolean;
  jobPreferences?: {
    jobTitle?: string;
    jobDescription?: string;
    jobType?: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship';
    experienceLevel?: 'entry' | 'junior' | 'mid' | 'senior' | 'manager' | 'head' | 'executive';
    currentSalary?: {
      max?: number;
      currency?: string;
      denomination?: 'thousand' | 'lakh' | 'crore';
      period?: 'yearly' | 'monthly';
    };
    expectedSalary?: {
      max?: number;
      currency?: string;
      denomination?: 'thousand' | 'lakh' | 'crore';
      period?: 'yearly' | 'monthly';
    };
    skills?: string[];
    location?: string[];
    workMode?: 'remote' | 'onsite' | 'hybrid';
    noticeType?: 'immediate' | '15-days' | '30-days' | '60-days' | '90-days';
    resume?: {
      id?: string;
      url?: string;
      originalName?: string;
    } | null;
  };
  
  // Job preference fields (sent separately when isLookingForJob is true)
  jobTitle?: string;
  jobDescription?: string;
  jobType?: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship';
  experienceLevel?: 'entry' | 'mid' | 'senior' | 'executive';
  currentSalary?: {
    max?: number;
    currency?: string;
    denomination?: 'thousand' | 'lakh' | 'crore';
    period?: 'yearly' | 'monthly';
  };
  expectedSalary?: {
    max?: number;
    currency?: string;
    denomination?: 'thousand' | 'lakh' | 'crore';
    period?: 'yearly' | 'monthly';
  };
  skills?: string[];
  location?: string[];
  workMode?: 'remote' | 'onsite' | 'hybrid';
  noticeType?: 'immediate' | '15-days' | '30-days' | '60-days' | '90-days';
  resume?: {
    id?: string;
    url?: string;
    originalName?: string;
  } | null;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: User;
  };
}

export interface Designation {
  _id: string;
  name: string;
}

export interface DesignationsResponse {
  success: boolean;
  message: string;
  data: {
    designations: Designation[];
  };
}

export interface RegisterRequest {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  designation: string;
  city: string;
  state: string;
  whatsappNumber: string;
  aboutMe?: string;
  socialMediaLinks?: string[];
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  data: {
    token: string;
    user: User;
  };
}

export interface ProfileResponse extends User {
  success?: boolean;
  message?: string;
  data?: {user:User}
}

export interface ApiResponse<T = any> {
  data?: T;
  message: string;
  status?: number;
  success?: boolean;
}

export interface LoadingState {
  isLoading: boolean;
  backdrop?: boolean;
}

export interface ThemeMode {
  mode: 'light' | 'dark';
  colors: {
    primary: string;
    secondary: string;
  };
}

export interface FileUploadResponse {
  id: string;
  filename: string;
  url: string;
  size: number;
  mimetype: string;
}

export interface FeedType {
  _id: string;
  name: string;
  isActive: boolean;
}

export interface FeedTypesResponse {
  // success: boolean;
  // message: string;
  // data: {
    feedTypes: FeedType[];
  // };
}

export interface MediaItem {
  id: string;
  mediaFileId?:string;
  type: 'image' | 'document' | 'video';
  url: string;
  originalName: string;
  mimeType: string;
  thumbnail?: string;
  size: number;
  file?: File;
}

export interface LinkPreview {
  title: string;
  description: string;
  image: string;
  url: string;
}

export interface PollOption {
  text: string;
  votes?: number;
}

export interface Poll {
  question: string;
  options: PollOption[];
  expiresAt?: string;
}

export interface Feed {
  _id: string;
  userId: {
    _id: string;
    email: string;
    name: string;
    isLookingForJob:boolean;
    designation?:string;
    profilePicture?: {
      id: string;
      _id?:string;
      url: string;
      originalName: string;
    };
  };
  feedType: FeedType | null;
  content: string;
  title?: string;
  media?: MediaItem[];
  location?: string;
  linkPreview?: LinkPreview;
  visibility: 'public' | 'private' | 'connections';
  tags: string[];
  poll?: Poll;
  userHasVoted?: boolean;
  userVoteIndex?: number;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  shares?: number;
  stats?: {
    comments: number;
    likes: number;
    shares: number;
    views: number;
    bookmarks: number;
  };
  relationshipStatus: {
    isFollowing: boolean;
    hasRequestPending: boolean;
    isRequestSent:boolean;
  };
  // Like state for current user
  isLiked?: boolean;
  isMeLiked?: boolean;
  likesCount?: number;
  likedUsers?: Array<{
    _id: string;
    name: string;
    email: string;
    profilePicture?: {
      url: string;
    };
  }>;
  // Application count for job feeds
  applicationCount?: number;
  commentCount?: number;
  likeCount?: number;
  // Feed type specific details (both field names for compatibility)
  feedSpecificDetails?: JobDetails | JobSeekerDetails | any; // Can be different types based on feedType
  feedSpecificData?: JobDetails | JobSeekerDetails | any; // Alternative field name from API
}

export interface NewsThumbnail {
  mediaFileId?: string;
  type?: string;
  url?: string;
  fileName?: string;
  contentType?: string;
  size?: number;
}

export interface News {
  _id: string;
  userId: {
    _id: string;
    name: string;
    profilePicture?: {
      url: string;
    };
  };
  title: string;
  content: string;
  slateContent: string;
  summary?: string;
  thumbnail?: NewsThumbnail | string | null;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
}
// Job-specific details interface
export interface JobDetails {
  _id: string;
  feedId: string;
  feedType: string;
  userId: {
    _id: string;
    email: string;
    name: string;
  };
  isApplied?:boolean;
  jobTitle: string;
  jobDescription: string;
  jobCategory?: string;
  jobCategoryId?: {
    _id: string;
    name: string;
    description: string;
  };
  companyInfo?: {
    _id: string;
    companyname: string;
    // description: string;
    addressLine1?:string;
    addressLine2?:string;
    pincode?:string
    city: string;
    state: string;
    email: string;
    mobile: string;
  };
  jobType: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship';
  experienceLevel: 'entry' | 'junior' | 'mid' | 'senior' | 'manager' | 'head' | 'executive';
  noOfVacancy: number;
  salary?: {
    min?: number;
    max?: number;
    currency?: string;
    denomination?: 'thousand' | 'lakh' | 'crore';
    period?: 'hourly' | 'monthly' | 'yearly';
  };
  skills: string[];
  location: string[];
  workMode: 'remote' | 'onsite' | 'hybrid';
  noticeType: 'immediate' | '15-days' | '30-days' | '60-days' | '90-days';
  applicationDeadline?: string;
  contactDetails?: {
    mobile?: string;
    email?: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Job seeker specific details interface
export interface JobSeekerDetails {
  _id: string;
  feedId: string;
  feedType: string;
  userId: {
    _id: string;
    email: string;
    name: string;
    profilePictureId?: string;
    designation?:string
  };
  jobTitle: string;
  jobDescription: string;
  jobType: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship';
  experienceLevel: 'entry' | 'junior' | 'mid' | 'senior' | 'manager' | 'head' | 'executive';
  currentSalary?: {
    max?: number;
    currency?: string;
    denomination?: 'thousand' | 'lakh' | 'crore';
    period?: 'yearly' | 'monthly';
  };
  expectedSalary?: {
    max?: number;
    currency?: string;
    denomination?: 'thousand' | 'lakh' | 'crore';
    period?: 'yearly' | 'monthly';
  };
  skills: string[];
  location: string[];
  workMode: 'remote' | 'onsite' | 'hybrid';
  noticeType: 'immediate' | '15-days' | '30-days' | '60-days' | '90-days';
  resumeId?: string | {
    id: string;
    url: string;
    originalName: string;
    mimeType?: string;
    size?: number;
  } | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeedRequest {
  feedType: string;
  title?: string;
  content: string;
  media?: MediaItem[];
  location?: string;
  linkPreview?: LinkPreview;
  visibility: 'public' | 'private' | 'connections';
  tags: string[];
  poll?: Poll;
  isAdminNotify?: boolean;
  feedSpecificData?: any; // Job-specific data or other feed type specific data
}

export interface FeedListParams {
  feedType?: string;
  visibility?: 'public' | 'private' | 'connections';
  tags?: string;
  search?: string;
  type?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  sort?: string;
  hasAttachment?: boolean;
  // Job-specific filters
  location?: string;
  experienceLevel?: string;
  jobType?: string;
  minSalary?: string;
  maxSalary?: string;
  salaryDenomination?: string;
  salaryPeriod?: string;
}

export interface FeedListResponse {
  // success: boolean;
  // message: string;
  // data: {
    feeds: Feed[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  // };
}

export interface CreateFeedResponse {
  success: boolean;
  message: string;
  data: {
    feed: Feed;
  };
}


export interface FeedState {
  feedTypes: FeedType[];
  feeds: Feed[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  } | null;
  isLoading: boolean;
  isFeedsLoading: boolean;
  isCreating: boolean;
  error: string | null;
}

// Comment Types
export interface CommentUser {
  _id: string;
  email: string;
  name: string;
  isEmailVerified: boolean;
  userId?: string;
  isLookingForJob:boolean;
  profilePictureId?: {
    _id: string;
    originalName: string;
    url: string;
    mimeType: string;
    size: number;
  } | null;
}

export interface CommentMention {
  userId: string;
  username: string;
}

export interface Comment {
  _id: string;
  feedId: string;
  userId: CommentUser;
  content: string;
  parentCommentId: string | null;
  isEdited: boolean;
  isDeleted: boolean;
  mentions: CommentMention[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  stats?: {
    replies: number;
    likes: number;
  };
  replies?: Comment[];
}

export interface CreateCommentRequest {
  feedId: string;
  content: string;
  parentCommentId?: string | null;
  mentions?: CommentMention[];
}

export interface UpdateCommentRequest {
  content: string;
  mentions?: CommentMention[];
}

export interface CreateCommentResponse {
  success: boolean;
  message: string;
  data: {
    comment: Comment;
  };
}

export interface GetCommentsResponse {
  success: boolean;
  message: string;
  data: {
    comments: Comment[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  };
}

export interface CommentListParams {
  page?: number;
  limit?: number;
  sort?: string;
  includeReplies?: boolean;
}

export interface CommentState {
  currentFeedId: string | null;
  comments: Comment[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  } | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

export interface JobCategory {
  _id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateJobCategoryRequest {
  name: string;
  description: string;
}

export interface UpdateJobCategoryRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface JobCategoryListParams {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface JobCategoryResponse {
  success: boolean;
  message: string;
  data: {
    jobCategory: JobCategory;
  };
}

export interface JobCategoryListResponse {
  success: boolean;
  message: string;
  data: {
    jobCategories: JobCategory[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

export interface JobCategoryState {
  jobCategories: JobCategory[];
  currentJobCategory: JobCategory | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

export interface Company {
  _id: string;
  name: string;
  description: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  gst: string;
  email: string;
  mobile: string;
  isActive: boolean;
  createdBy: {
    _id: string;
    email: string;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateCompanyRequest {
  name: string;
  description: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  gst: string;
  email: string;
  mobile: string;
}

export interface UpdateCompanyRequest {
  name?: string;
  description?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  gst?: string;
  email?: string;
  mobile?: string;
  isActive?: boolean;
}

export interface CompanyListParams {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface CompanyResponse {
  success: boolean;
  message: string;
  data: {
    company: Company;
  };
}

export interface CompanyListResponse {
  success: boolean;
  message: string;
  data: {
    companies: Company[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

export interface CompanyState {
  companies: Company[];
  currentCompany: Company | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

// ─── Event Module Types ───────────────────────────────────────────────────────

export const EVENT_CATEGORIES = [
  'Training & Workshops',
  'Webinars & Online Sessions',
  'Conferences & Summits',
  'Networking Events',
  'Competitions & Challenges',
  'Panel Discussions & Talks',
  'Career & Campus Events',
  'Awards & Recognition',
  'Corporate & Internal Programs',
  'Community & Meetups',
] as const;

export type EventCategory = typeof EVENT_CATEGORIES[number];
export type EventMode = 'online' | 'in_person';
export type EventVisibility = 'public' | 'private';
export type TicketType = 'free' | 'paid';

export interface EventMedia {
  mediaFileId?: string;
  type?: string;
  url?: string;
  fileName?: string;
  contentType?: string;
  thumbnail?: string;
  size?: number;
}

export interface EventOrganizer {
  _id: string;
  name: string;
  email: string;
}

export interface EventAddress {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  googleMapsLink?: string;
}

export interface ConferenceEvent {
  _id: string;
  eventName: string;
  eventBanner?: EventMedia;
  organizer: EventOrganizer | string;
  businessId?: string | {
    _id: string;
    businessName: string;
  };
  category: EventCategory;
  startDateTime: string;
  endDateTime?: string;
  registrationDeadline?: string;
  eventMode: EventMode;
  eventLink?: string;
  venueName?: string;
  fullAddress?: EventAddress;
  description: string;
  eventVisibility: EventVisibility;
  certificateProvided: boolean;
  ticketType: TicketType;
  price?: number;
  totalSeats?: number;
  isActive: boolean;
  isEnrolled?: boolean;
  enrollmentCount?: number;
  views?: number;
  createdAt?: string;
}

export interface CreateEventRequest {
  businessId: string;
  eventName: string;
  eventBanner?: EventMedia;
  category: EventCategory;
  startDateTime: string;
  endDateTime?: string;
  registrationDeadline?: string;
  eventMode: EventMode;
  eventLink?: string;
  venueName?: string;
  fullAddress?: EventAddress;
  description: string;
  eventVisibility?: EventVisibility;
  certificateProvided?: boolean;
  ticketType: TicketType;
  price?: number;
  totalSeats?: number;
}

export type UpdateEventRequest = Partial<CreateEventRequest>;

export interface EventListParams {
  search?: string;
  page?: number;
  limit?: number;
  category?: EventCategory;
  eventMode?: EventMode;
  ticketType?: TicketType;
  eventVisibility?: EventVisibility;
  upcoming?: boolean;
  organizer?: string;
  enroll?: string;
  city?: string;
  state?: string;
  location?: string;
  startDate?: string;
  endDate?: string;
}

export interface EnrolledUser {
  _id: string;
  name: string;
  email: string;
  designation?: string;
  city?: string;
  state?: string;
  whatsappNumber?: string;
  enrolledAt: string;
}

export interface EnrolledUsersResponse {
  success: boolean;
  message: string;
  data: {
    event?: string;
    users: EnrolledUser[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  };
}

export interface EventResponse {
  success: boolean;
  message: string;
  data: ConferenceEvent;
}

export interface EventListResponse {
  success: boolean;
  message: string;
  data: {
    events: ConferenceEvent[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

export interface EventState {
  events: ConferenceEvent[];
  currentEvent: ConferenceEvent | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isEnrolling: boolean;
  isMarkingInactive: boolean;
  error: string | null;
  enrollingEventId: string | null;
}

// ─── Business Module Types ───────────────────────────────────────────────────

export const BUSINESS_CATEGORIES = [
  'Technology & IT',
  'BPO & ITES',
  'Finance & BFSI',
  'Manufacturing & Industrial',
  'Healthcare & Pharma',
  'Education & Training',
  'Retail & E-commerce',
  'Real Estate & Construction',
  'Logistics & Transportation',
  'Media & Entertainment',
  'Energy & Utilities',
  'Hospitality & Tourism',
  'Restaurant & Food',
  'Professional Services',
  'HR & Recruitment Services',
  'Startups & MSMEs',
  'Non-Profit / NGO',
  'Others',
] as const;

export type BusinessCategory = string;

export const BUSINESS_SERVICES = [
  'Recruitment & Talent Acquisition',
  'Staffing (Permanent / Contract / Temporary)',
  'Executive Search & Leadership Hiring',
  'Recruitment Process Outsourcing (RPO)',
  'Recruitment Marketing',
  'Talent Intelligence & Mapping',
  'Gig / Freelance Hiring',
  'Workforce Planning',
  'Manpower Outsourcing',
  'Industrial / Blue Collar Hiring',
  'Employee Leasing',
  'Vendor Management',
  'Apprenticeship Program Management',
  'Payroll Processing',
  'Global Payroll Management',
  'Payroll Compliance & Filings',
  'Statutory Compliance (PF, ESI, PT, etc.)',
  'Labour Law Compliance',
  'Tax & Salary Structuring',
  'Compliance Audit & Risk Management',
  'Labour Law Advisory',
  'POSH Compliance & Training',
  'Employment Contracts & Documentation',
  'Industrial Relations Management',
  'Disciplinary & Legal Case Handling',
  'HR Outsourcing (HRO)',
  'HR Administration',
  'Employee Lifecycle Management',
  'HR Shared Services',
  'Attendance & Leave Management',
  'Onboarding & Offboarding',
  'Corporate Training Programs',
  'Learning & Development',
  'Soft Skills Training',
  'Leadership Development',
  'eLearning & LMS',
  'HR Strategy & Transformation',
  'Organizational Design & Development',
  'Change Management Consulting',
  'HR Policy Design & Implementation',
  'HR Advisory Services',
  'HR Due Diligence (M&A)',
  'Startup / MSME HR Setup',
  'Performance Management System (PMS)',
  'OKR & KPI Implementation',
  'Succession Planning',
  'Talent Development Programs',
  '360 Degree Feedback',
  'Employee Engagement Programs',
  'Employee Engagement Surveys',
  'Rewards & Recognition Programs',
  'Employee Experience Design',
  'Workplace Culture Development',
  'HRMS Implementation',
  'Applicant Tracking System (ATS)',
  'Payroll Software Solutions',
  'HR Analytics & Reporting',
  'People Analytics',
  'Digital HR Transformation',
  'Background Verification (BGV)',
  'Identity & Address Verification',
  'Employment Verification',
  'Criminal Record Checks',
  'Education Verification',
  'Compensation Benchmarking',
  'Salary Surveys & Reports',
  'CTC Structuring',
  'Variable Pay & Incentives',
  'Employee Benefits Administration',
  'Insurance & Benefits Management',
  'Employer of Record (EOR)',
  'International Hiring & Compliance',
  'Global Mobility Services',
  'Expat Payroll & Compliance',
  'Remote Workforce Management',
  'Diversity, Equity & Inclusion (DEI)',
  'HR Audit & Diagnostics',
  'Employer Branding',
  'HR Support Services',
  'Resume Writing',
  'Interview Coaching',
  'Career Counseling',
  'HR Mentorship',
  'HR Events & Conferences',
  'HR Webinars & Workshops',
  'HR Networking Communities',
] as const;

export type BusinessService = string;

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'in_review' | 'clarification_needed';
export type DocumentType = 'GST' | 'License' | 'Registration' | 'Other';

export interface BusinessHours {
  day: string;
  openTime?: string;
  closeTime?: string;
  isClosed: boolean;
}

export interface FileObject {
  _id: string;
  url: string;
  originalName: string;
  mimeType: string;
  size: number;
}

export interface BusinessDocument {
  documentType: DocumentType;
  fileId: string | FileObject;
}

export interface SocialMedia {
  instagram?: string;
  facebook?: string;
  youtube?: string;
  twitter?: string;
}

export interface GeoLocation {
  latitude?: number;
  longitude?: number;
}

export interface HeadOfficeAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  landmark?: string;
}

export interface Business {
  _id: string;
  businessName: string;
  businessUsername: string;
  businessCategory: BusinessCategory | BusinessCategory[];
  businessServices?: BusinessService | BusinessService[];
  description?: string;
  logoId?: string | FileObject;
  bannerId?: string | FileObject;
  email: string;
  website?: string;
  contactNumber: string;
  whatsappNumber?: string;
  landlineNumber?: string;
  additionalPhoneNumbers?: string[];
  rating: number;
  ratingsCount: number;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  landmark?: string;
  geoLocation?: GeoLocation;
  businessHours?: BusinessHours[];
  socialMedia?: SocialMedia;
  // New fields
  gstNumber?: string;
  panNumber?: string;
  companyStartDate?: string;
  numberOfEmployees?: string;
  googleMyBusinessLink?: string;
  headOfficeAddress?: HeadOfficeAddress;
  isVerified: boolean;
  documents?: BusinessDocument[];
  ownerName: string;
  ownerIdProofId?: string | FileObject;
  userId: string | {
    _id: string;
    name: string;
    email: string;
  };
  isActive: boolean;
  isBlocked: boolean;
  isApproved: boolean;
  isSuspended: boolean;
  approvalStatus: ApprovalStatus;
  rejectionReason?: string;
  clarificationNote?: string;
  approvedBy?: string | {
    _id: string;
    name: string;
    email: string;
  };
  approvedAt?: string;
  rejectedBy?: string | {
    _id: string;
    name: string;
    email: string;
  };
  rejectedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBusinessRequest {
  businessName: string;
  businessUsername: string;
  businessCategory: BusinessCategory | BusinessCategory[];
  businessServices?: BusinessService | BusinessService[];
  description?: string;
  logoId?: string;
  bannerId?: string;
  email: string;
  website?: string;
  contactNumber: string;
  whatsappNumber?: string;
  landlineNumber?: string;
  additionalPhoneNumbers?: string[];
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  pincode: string;
  landmark?: string;
  geoLocation?: GeoLocation;
  businessHours?: BusinessHours[];
  socialMedia?: SocialMedia;
  // New fields
  gstNumber?: string;
  panNumber?: string;
  companyStartDate?: string;
  numberOfEmployees?: string;
  googleMyBusinessLink?: string;
  headOfficeAddress?: HeadOfficeAddress;
  documents?: Array<{
    documentType: DocumentType;
    fileId: string;
  }>;
  ownerName: string;
  ownerIdProofId?: string;
}

export interface UpdateBusinessRequest {
  businessName?: string;
  businessUsername?: string;
  businessCategory?: BusinessCategory | BusinessCategory[];
  businessServices?: BusinessService | BusinessService[];
  description?: string;
  logoId?: string;
  bannerId?: string;
  email?: string;
  website?: string;
  contactNumber?: string;
  whatsappNumber?: string;
  landlineNumber?: string;
  additionalPhoneNumbers?: string[];
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  pincode?: string;
  landmark?: string;
  geoLocation?: GeoLocation;
  businessHours?: BusinessHours[];
  socialMedia?: SocialMedia;
  // New fields
  gstNumber?: string;
  panNumber?: string;
  companyStartDate?: string;
  numberOfEmployees?: string;
  googleMyBusinessLink?: string;
  headOfficeAddress?: HeadOfficeAddress;
  documents?: Array<{
    documentType: DocumentType;
    fileId: string;
  }>;
  ownerName?: string;
  ownerIdProofId?: string;
}

export interface BusinessListParams {
  search?: string;
  businessCategory?: string;
  approvalStatus?: ApprovalStatus;
  city?: string;
  state?: string;
  isVerified?: boolean;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface BusinessResponse {
  success: boolean;
  message: string;
  data: {
    business: Business;
  };
}

export interface BusinessListResponse {
  success: boolean;
  message: string;
  data: {
    businesses: Business[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
}

export interface BusinessState {
  businesses: Business[];
  currentBusiness: Business | null;
  myBusinesses: Business[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNext: boolean;
    hasPrev: boolean;
  } | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
}

// ─── Service/Product Module Types ─────────────────────────────────────────────

export type ServiceType = 'product' | 'service';

export interface ServiceProductMedia {
  mediaFileId?: string;
  url?: string;
  fileName?: string;
  contentType?: string;
  size?: number;
}

export interface ServiceProduct {
  _id: string;
  businessId: string | {
    _id: string;
    businessName: string;
    logoId?: string;
  };
  userId?: string | {
    _id: string;
    email: string;
    name: string;
  };
  serviceType: ServiceType;
  title: string;
  price?: number;
  description?: string;
  productImage?: ServiceProductMedia;
  brochure?: ServiceProductMedia;
  isPromoted: boolean;
  promotionStartDate?: string;
  promotionEndDate?: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServiceProductRequest {
  businessId: string;
  serviceType: ServiceType;
  title: string;
  price?: number;
  description?: string;
  productImage?: ServiceProductMedia;
  brochure?: ServiceProductMedia;
}

export interface UpdateServiceProductRequest {
  serviceType?: ServiceType;
  title?: string;
  price?: number;
  description?: string;
  productImage?: ServiceProductMedia;
  brochure?: ServiceProductMedia;
  isActive?: boolean;
}

export interface ServiceProductListParams {
  search?: string;
  serviceType?: ServiceType;
  businessId?: string;
  promoted?: boolean;
  minPrice?: number;
  maxPrice?: number;
  isActive?: boolean;
  page?: number;
  limit?: number;
  sort?: string;
}

export interface ServiceProductResponse {
  success: boolean;
  message: string;
  data: ServiceProduct;
}

export interface ServiceProductListResponse {
  success: boolean;
  message: string;
  data: {
    serviceProducts: ServiceProduct[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  };
}

// ─── Business Course Module Types ─────────────────────────────────────────────

export type CourseLevel = 'Beginner' | 'Intermediate' | 'Advanced' | 'All';
export type CourseLanguage = 'English' | 'Tamil' | 'Bilingual';
export type CourseType = 'Free' | 'Paid';
export type CourseAccessType = 'Lifetime' | 'Limited' | 'Subscription';

export interface BusinessCourseMedia {
  mediaFileId?: string;
  type?: string;
  url?: string;
  fileName?: string;
  contentType?: string;
  thumbnail?: string;
  size?: number;
}

export interface BusinessCourse {
  _id: string;
  businessId: string | {
    _id: string;
    businessName: string;
    businessUsername?: string;
    businessCategory?: string | string[];
    logoId?: string;
    email?: string;
    contactNumber?: string;
    whatsappNumber?: string;
    city?: string;
    state?: string;
  };
  userId?: string | {
    _id: string;
    name: string;
    email: string;
    profileImage?: string;
  };
  courseTitle: string;
  level: CourseLevel;
  language?: CourseLanguage;
  courseThumbnail?: BusinessCourseMedia;
  description: string;
  duration?: string;
  numberOfModules?: number;
  certificationAvailable?: boolean;
  hashTags?: string[];
  brochure?: BusinessCourseMedia;
  courseType: CourseType;
  price?: number;
  discount?: number;
  accessType?: CourseAccessType;
  isPromoted?: boolean;
  promotionStartDate?: string;
  promotionEndDate?: string;
  isActive: boolean;
  isDeleted: boolean;
  isCurrentlyPromoted?: boolean;
  finalPrice?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBusinessCourseRequest {
  businessId: string;
  courseTitle: string;
  level: CourseLevel;
  language?: CourseLanguage;
  courseThumbnail?: BusinessCourseMedia;
  description: string;
  duration?: string;
  numberOfModules?: number;
  certificationAvailable?: boolean;
  hashTags?: string[];
  brochure?: BusinessCourseMedia;
  courseType: CourseType;
  price?: number;
  discount?: number;
  accessType?: CourseAccessType;
}

export interface UpdateBusinessCourseRequest {
  courseTitle?: string;
  level?: CourseLevel;
  language?: CourseLanguage;
  courseThumbnail?: BusinessCourseMedia;
  description?: string;
  duration?: string;
  numberOfModules?: number;
  certificationAvailable?: boolean;
  hashTags?: string[];
  brochure?: BusinessCourseMedia;
  courseType?: CourseType;
  price?: number;
  discount?: number;
  accessType?: CourseAccessType;
  isActive?: boolean;
  businessId?: string;
  isPromoted?: boolean;
  promotionStartDate?: string;
  promotionEndDate?: string;
}

export interface BusinessCourseListParams {
  search?: string;
  q?: string;
  businessId?: string;
  level?: CourseLevel;
  language?: CourseLanguage;
  courseType?: CourseType;
  accessType?: CourseAccessType;
  certificationAvailable?: boolean;
  promoted?: boolean;
  minPrice?: number;
  maxPrice?: number;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface BusinessCourseResponse {
  success: boolean;
  message: string;
  data: BusinessCourse;
}

export interface BusinessCourseListResponse {
  success: boolean;
  message: string;
  data: {
    businessCourses: BusinessCourse[];
    pagination: {
      current: number;
      pages: number;
      total: number;
      limit: number;
    };
  };
}